import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Award, Download, ShieldCheck, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const LADDER = ["NOT CERTIFIED", "TRAINING REQUIRED", "TRAINING IN PROGRESS", "ASSESSMENT PENDING", "AWAITING CERTIFICATION", "CERTIFIED"];
const daysUntil = (iso) => iso ? Math.ceil((new Date(iso) - new Date()) / 86400000) : null;

export default function ApplicatorCertification() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/applicator/certification").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <div className="text-gray-500">Loading…</div>;

  const certs = data.certificates || [];
  const services = data.services || [];

  const renew = async (sid) => {
    try { await api.post(`/applicator/certification/${sid}/renew`); toast.success(t("renewal_started") || "Renewal started"); navigate(`/applicator/training/${sid}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="applicator-certification-page">
      <div className="flex items-center gap-3 mb-6"><ShieldCheck className="text-[#C9A45C]" size={26} /><h1 className="serif-head text-3xl lg:text-4xl">{t("certification")}</h1></div>

      <p className="errah-label gold-text mb-3">{t("certificates")} ({certs.length})</p>
      {certs.length === 0 ? (
        <div className="hair-border bg-white/[0.02] p-6 text-sm text-gray-500 mb-8">Complete training and pass an assessment to earn your first certificate. <button onClick={() => navigate("/applicator/training")} className="text-[#C9A45C] hover:underline">{t("my_training")} →</button></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          {certs.map((c) => {
            const expired = c.status !== "ACTIVE" || (c.valid_until && c.valid_until < new Date().toISOString());
            return (
              <div key={c.certificate_id} data-testid={`cert-${c.certificate_id}`} className="relative hair-border bg-gradient-to-br from-[#141414] to-[#0A0A0A] border-[#C9A45C]/30 p-6 overflow-hidden">
                <div className="absolute top-4 right-4"><Award size={22} className={expired ? "text-red-400" : "text-[#C9A45C]"} /></div>
                <p className="errah-label gold-text">{t("certified_service")}</p>
                <p className="font-display text-xl mt-1">{c.service_name}</p>
                <div className="mt-4 space-y-1.5 text-sm">
                  <p className="text-gray-500 text-xs">{t("certificate_id")}</p>
                  <p className="font-mono text-[#E4C987]">{c.certificate_id}</p>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400"><Clock size={13} /> {t("valid_until")}: {c.valid_until ? c.valid_until.slice(0, 10) : t("no_expiry")}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a href={`${API}/certificates/${c.certificate_id}/pdf`} target="_blank" rel="noreferrer" data-testid={`download-cert-${c.certificate_id}`} className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-2 text-xs hover:bg-white hover:text-black transition-colors"><Download size={13} /> {t("download_certificate")}</a>
                  <button onClick={() => navigate(`/verify-certificate/${c.certificate_id}`)} data-testid={`verify-cert-${c.certificate_id}`} className="inline-flex items-center gap-1.5 border border-[#C9A45C]/40 text-[#E4C987] px-3 py-2 text-xs hover:bg-[#C9A45C] hover:text-black transition-colors"><ShieldCheck size={13} /> {t("verify_certificate")}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="errah-label gold-text mb-3">Certification Status</p>
      <div className="space-y-2" data-testid="cert-status-list">
        {services.map((s) => {
          const step = Math.max(0, LADDER.indexOf(s.cert_status));
          const dleft = daysUntil(s.cert_valid_until);
          const expiringSoon = s.cert_status === "CERTIFIED" && dleft !== null && dleft <= 60;
          const expired = s.cert_status === "EXPIRED";
          return (
            <div key={s.service_id} className="hair-border bg-white/[0.02] p-4" data-testid={`cert-svc-${s.service_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm">{s.service_name}</p>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2.5 py-1 border ${s.cert_status === "CERTIFIED" ? "text-emerald-400 border-emerald-500/40" : expired ? "text-red-400 border-red-500/40" : "text-[#E4C987] border-[#C9A45C]/40"}`}>{s.cert_status}</span>
                  {(expiringSoon || expired) && (
                    <button onClick={() => renew(s.service_id)} data-testid={`renew-${s.service_id}`} className="text-[10px] inline-flex items-center gap-1 bg-[#C9A45C] text-black px-2.5 py-1 font-semibold hover:bg-[#E4C987] transition-colors"><RefreshCw size={11} /> {t("renew_certification") || "Renew"}</button>
                  )}
                </div>
              </div>
              <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-[#C9A45C]" style={{ width: `${(step / (LADDER.length - 1)) * 100}%` }} /></div>
              {s.cert_valid_until && s.cert_status === "CERTIFIED" && (
                <p className={`text-[11px] mt-1 ${expiringSoon ? "text-[#E4C987]" : "text-gray-500"}`}>{t("valid_until")}: {s.cert_valid_until.slice(0, 10)}{dleft !== null && dleft >= 0 ? ` · ${dleft} ${t("days_remaining") || "days remaining"}` : ""}</p>
              )}
              {expired && <p className="text-[11px] text-red-400 mt-1">Certification expired — renew to receive {s.service_name} jobs again.</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
